import React, { useState } from 'react';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';

import adminApi, { pagingParams } from '../../Api/adminApi';
import PaginatedTable from '../../MyComponents/PaginatedTable/PaginatedTable';
import store from '../../redux/store';
import { showError, showSuccess } from '../../redux/actions';
import { COLORS2 } from '../../constants';
import useAdminStyles from './adminStyles';
import useAdminQuery from './useAdminQuery';
import StateCard from './StateCard';
import { errorMessage, fmtDate, fmtDateTime } from './adminFormat';

const columns = [
    { id: 'SN', label: 'Serial number', sortable: false },
    { id: 'email', label: 'Linked account', render: (val) => val || '—' },
    { id: 'authtype', label: 'Login type', sortable: false, render: (val) => val || '—' },
    { id: 'logincount', label: 'Logins', numeric: true },
    { id: 'latestlogin', label: 'Latest login', render: (val) => fmtDateTime(val) },
    { id: 'createdAt', label: 'Registered', sortable: false, render: (val) => fmtDate(val) },
];

// The API sorts on latestLogin / loginCount / email; map the column ids.
const SORT_FIELD = { latestlogin: 'latestLogin', logincount: 'loginCount', email: 'email' };

// Mirrors the server-side rule, so obvious typos are caught before the request.
const SN_PATTERN = /^[A-Za-z0-9._-]{3,64}$/;

/**
 * Samsung whiteboard devices registered for the Samsung login flow, and the
 * account each one has been linked to. New serials are confirmed, validated
 * on both sides and rejected when already registered.
 */
const SamsungTab = () => {
    const classes = useAdminStyles();
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('latestlogin');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sn, setSn] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const query = pagingParams({ rowsPerPage, page, orderBy: SORT_FIELD[orderBy] || 'latestLogin', order });
    const { data, isLoading, isError, isForbidden, reload } = useAdminQuery(() => adminApi.getSamsungAccounts(query), [
        JSON.stringify(query),
    ]);

    const value = sn.trim();
    const valid = SN_PATTERN.test(value);

    const addSn = () => {
        if (!valid || busy) {
            return;
        }
        if (!window.confirm(`Register Samsung device "${value}"?\n\nThe serial is stored as typed and cannot be edited later.`)) {
            return;
        }
        setBusy(true);
        setError(null);
        adminApi
            .addSamsungSN(value)
            .then(() => {
                store.dispatch(showSuccess('ADMIN_SN_ADDED'));
                setSn('');
                reload();
            })
            .catch((err) => {
                console.error(err);
                setError(errorMessage(err));
                store.dispatch(showError('GENERIC_ERROR'));
            })
            .finally(() => setBusy(false));
    };

    if (isForbidden || (isError && !data)) {
        return <StateCard isError={isError} isForbidden={isForbidden} />;
    }

    return (
        <>
            <div className={classes.filters}>
                <TextField
                    className={classes.filterSearch}
                    variant="outlined"
                    size="small"
                    label="New device serial number"
                    value={sn}
                    error={!!value && !valid}
                    helperText={value && !valid ? '3-64 characters: letters, digits, dot, underscore or dash' : ' '}
                    onChange={(event) => setSn(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            addSn();
                        }
                    }}
                />
                <Button variant="contained" color="primary" disabled={busy || !valid} onClick={addSn}>
                    Register device
                </Button>
            </div>
            {error ? (
                <p style={{ margin: '0 0 12px', fontSize: '0.88rem', color: COLORS2.virtualRed }} role="alert">
                    {error}
                </p>
            ) : null}
            <PaginatedTable
                title={`Samsung devices (${data?.count || 0})`}
                headCells={columns}
                rows={data?.samsungDevices || []}
                rowCount={data?.count || 0}
                order={order}
                orderBy={orderBy}
                setOrder={(nextOrder, nextOrderBy) => {
                    setOrder(nextOrder);
                    setOrderBy(nextOrderBy);
                    setPage(0);
                }}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={(n) => {
                    setRowsPerPage(n);
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                isLoading={isLoading}
                emptyMessage="No Samsung devices registered."
            />
        </>
    );
};

export default SamsungTab;
